import { useCallback, useMemo, useState } from 'react';
import constate from '../../common/lib/constate';

function useTrim() {
  const [trimmingOn, setTrimmingOn] = useState(false);
  const toggleTrimmingOn = useCallback(state => {
    if (typeof state === 'boolean') setTrimmingOn(state);
    else setTrimmingOn(prevState => !prevState);
  }, []);

  const [startTrimStamp, setStartTrimStamp] = useState(0);
  const updateStartTrimStamp = useCallback(state => {
    if (typeof state === 'number') setStartTrimStamp(state);
  }, []);

  const [endTrimStamp, setEndTrimStamp] = useState(0);
  const updateEndTrimStamp = useCallback(state => {
    if (typeof state === 'number') setEndTrimStamp(state);
  }, []);

  const [currentProgress, setCurrentProgress] = useState(0);
  const updateCurrentProgress = useCallback(state => {
    if (typeof state === 'number') setCurrentProgress(state);
  }, []);

  const [trimmingVideoDuration, setVideoDuration] = useState(0);
  const updateTrimmingVideoDuration = useCallback(state => {
    if (typeof state === 'number') setVideoDuration(state);
  }, []);

  const [trimmingActionsBuffer, updateTrimmingActionsBuffer] = useState([]);
  const [currentActionIndex, setCurrentActionIndex] = useState(-1);
  const [updateEditor, setUpdateEditor] = useState(false);
  const toggleUpdateEditor = useCallback(state => {
    if (typeof state === 'boolean') setUpdateEditor(state);
    else setUpdateEditor(prevState => !prevState);
  }, []);

  const [controlInUse, setControlInUse] = useState(false);

  /**
   * action : {
   *     startTrim: ,
   *     endTrim: ,
   * }
   * */
  const addNewAction = useCallback(
    action => {
      // Previously user gone back to action from the buffer, and now adds a new action, that's why all actions after new one must be cleared
      if (currentActionIndex < trimmingActionsBuffer.length - 1) {
        updateTrimmingActionsBuffer(prevState => [...prevState.slice(0, currentActionIndex), action]);
      } else {
        updateTrimmingActionsBuffer(prevState => [...prevState, action]);
      }
      setCurrentActionIndex(prevState => prevState + 1);
    },
    [currentActionIndex, trimmingActionsBuffer]
  );

  const undoLastAction = useCallback(() => {
    if (currentActionIndex > 0) {
      setCurrentActionIndex(prevState => prevState - 1);
      setUpdateEditor(true);
    }
  }, [currentActionIndex]);

  const redoLastAction = useCallback(() => {
    if (currentActionIndex < trimmingActionsBuffer.length) {
      setCurrentActionIndex(prevState => prevState + 1);
      setUpdateEditor(true);
    }
  }, [currentActionIndex]);

  const [trimmingRevert, setTrimmingRevert] = useState(false);
  const toggleTrimmingRevert = useCallback(
    state => {
      if (typeof state === 'boolean') setTrimmingRevert(state);
      else setTrimmingRevert(prevState => !prevState);
    },
    [trimmingRevert]
  );

  const [trimmingProgress, setTrimmingProgress] = useState(0);
  const updateTrimmingProgress = useCallback(state => {
    if (typeof state === 'number') setTrimmingProgress(state);
  }, []);

  const [videoTrimmed, setVideoTrimmed] = useState(false);
  const updateVideoTrimmed = useCallback(
    state => {
      setVideoTrimmed(state);
    },
    [videoTrimmed]
  );

  const clearAfterTrimmingDone = useCallback(() => {
    updateTrimmingActionsBuffer([]);
    setCurrentActionIndex(-1);
    setTrimmingOn(false);
  }, [trimmingActionsBuffer, currentActionIndex, trimmingOn]);

  return useMemo(
    () => ({
      trimmingOn,
      toggleTrimmingOn,
      startTrimStamp,
      updateStartTrimStamp,
      endTrimStamp,
      updateEndTrimStamp,
      currentProgress,
      updateCurrentProgress,
      trimmingVideoDuration,
      updateTrimmingVideoDuration,
      trimmingRevert,
      toggleTrimmingRevert,
      trimmingActionsBuffer,
      currentActionIndex,
      addNewAction,
      undoLastAction,
      redoLastAction,
      updateEditor,
      toggleUpdateEditor,
      trimmingProgress,
      updateTrimmingProgress,
      videoTrimmed,
      updateVideoTrimmed,
      clearAfterTrimmingDone,
      controlInUse,
      setControlInUse,
    }),
    [
      trimmingOn,
      startTrimStamp,
      endTrimStamp,
      currentProgress,
      trimmingVideoDuration,
      trimmingRevert,
      trimmingActionsBuffer,
      currentActionIndex,
      updateEditor,
      trimmingProgress,
      videoTrimmed,
      controlInUse,
    ]
  );
}

export const useTrimContext = constate(useTrim);

const TrimContext = {
  Provider: useTrimContext.Provider,
};

export default TrimContext;
